import { IAirtableAttachment } from "@rogoag/airtable";
import {
	BoundaryChangeType,
	JobFlags,
	JobsPriority,
	FileOptionsGroup,
	GeoJSONOptionsGroup,
} from "../../../utilities/types";
import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { BaseDataModel } from "./_BaseDataModel";
import { DEBUG } from "../../../utilities/constants";
import { generateUUID } from "../../../utilities/utils";

export enum JobStatusNumber {
	Unready = 0,
	Ready = 1,
	Sampled = 2,
	DropShipped = 3,
	CSVUnsent = 4,
	Completed = 7,
}

export interface Job extends BaseDataModel {
	readonly name: string; // formula
	readonly edits: string; // formula

	readonly status: string; // formula
	readonly statusNumber: JobStatusNumber; // formula
	sampleChanges: string;
	boundaryAcres: number;
	lastCrop?: string;
	nextCrop?: string;
	yieldGoal: string;
	numberOfSamples?: number;

	fieldId?: string;
	readonly fieldName: string; // GET: formula
	eventId: string;

	jobFlags: JobFlags[];
	boundaryChangeType?: BoundaryChangeType;
	boundaryChangeNotes?: string;
	fieldPriority: JobsPriority;

	readonly testPackage: string; // lookup from Deal
	readonly season: string; // lookup from Deal
	readonly depth: number; // lookup from Deal
	readonly density?: number; // lookup from Deal
	readonly frequencyOfMicros?: number; // lookup from Deal
	readonly labName?: string; // lookup from Deal
	microsList?: string;
	isBillingApproved: boolean;
	isMarkedReady: boolean;
	isBoundaryChanged: boolean;
	isBoundaryApproved: boolean;
	isPointsChanged: boolean;
	isTestJob?: boolean;
	isPointsAttached: boolean;
	isReadyToSample: boolean;

	dateCreated: Date;
	dateFileLastEdited?: Date;
	dateSampled?: Date;
	dateShipped?: Date;
	dateLabResultsSent?: Date;
	dateFieldReady?: Date;
	dateFieldReadyEstimated?: Date;

	submissionNotes?: string;
	submitterEmail?: string;
	submitterName?: string;

	labSubmittalId: string;
	labAccountNumber?: string;
	labSubmissionCode?: string;
	labInstructions: string;

	attachments: {
		pointsSHP?: IAirtableAttachment[];
		boundarySHP?: IAirtableAttachment[];
		boundaryGeoJSON?: IAirtableAttachment[];
		zonesSHP?: IAirtableAttachment[];
		executedPointsSHP?: IAirtableAttachment[];
		executedBoundarySHP?: IAirtableAttachment[];
		executedPointsBoundarySHP?: IAirtableAttachment[];
		labPDF?: IAirtableAttachment[];
		labCSV?: IAirtableAttachment[];
		labResults?: IAirtableAttachment[];
		fieldNotes?: IAirtableAttachment[];
	};

	/**
	 * Contains the file data itself. Primarily intended for creating new jobs.
	 */
	files: {
		pointsSHP?: File[];
		pointsGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		boundarySHP?: File[];
		boundaryGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		zonesSHP?: File[];
		zonesGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		executedPointsSHP?: File[];
		executedBoundarySHP?: File[];
		executedPointsBoundarySHP?: File[];
		labPDF?: File[];
		labCSV?: File[];
		labResults?: File[];
		fieldNotes?: File[];
	};

	/**
	 * Contains the file data itself. Primarily intended for display in ImportTable
	 */
	fileOptions: {
		pointsSHP?: FileOptionsGroup[];
		centroidsSHP?: File[];
		coresSHP?: File[];
		boundarySHP?: FileOptionsGroup[];
		zonesSHP?: FileOptionsGroup[];

		boundaryOptions?: GeoJSONOptionsGroup[];
		pointsOptions?: GeoJSONOptionsGroup[];
		zonesOptions?: GeoJSONOptionsGroup[];

		pointsGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		centroidsGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		coresGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		boundaryGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;
		zonesGeoJSON?: FeatureCollection<Geometry, GeoJsonProperties>;

		executedPointsSHP?: File[];
		executedBoundarySHP?: File[];
		executedPointsBoundarySHP?: File[];
		labPDF?: File[];
		labCSV?: File[];
		labResults?: File[];
		fieldNotes?: File[];
	};

	readonly companyName: string; // lookup from Company
	readonly farmName?: string; // formula
	readonly growerName: string; // lookup from Grower
	growerPhoneNumber?: string;

	branchIds: string[];
	regionIds: string[];
	dealIds: string[];
	growerIds: string[];
	salespersonIds: string[];

	rogoAppVersion?: string; // also used for creating new jobs

	// used for creating new jobs
	importFieldName?: string;
	importDepth?: number;
	importDensity?: number;
	importLabName?: string;
	importFarmName?: string;
	importGrowerName?: string;

	externalId?: string;
	externalTestPackageId?: string;
	externalFrequencyOfMicros?: number;
	externalLabId?: string;
	externalFarmId?: string;
	externalGrower?: {
		id: string;
		name: string;
	};
	newSalesperson?: {
		name: string;
		phone: string;
	};
	collectBoundary?: boolean;
}

export function defaultJobForImport(
	fieldName?: string,
	depth?: number,
	density?: number,
	growerName?: string,
	frequencyOfMicros?: number,
	testPackage?: string,
	farmName?: string,
	labName?: string,
): Job {
	return {
		id: generateUUID(),
		name: "",
		fieldName: fieldName ?? "",
		importFieldName: fieldName ?? "",
		growerName: growerName ?? "",
		importGrowerName: growerName ?? "",
		depth: depth ?? 0,
		importDepth: depth ?? 0,
		farmName: farmName ?? "",
		importFarmName: farmName ?? "",
		labName: labName ?? "",
		importLabName: labName ?? "",
		frequencyOfMicros: frequencyOfMicros ?? undefined,
		externalFrequencyOfMicros: frequencyOfMicros ?? undefined,
		testPackage: testPackage ?? "",
		externalTestPackageId: testPackage ?? "",
		branchIds: [],
		regionIds: [],
		boundaryChangeType: undefined,
		boundaryChangeNotes: undefined,
		dealIds: [],
		externalId: "",
		fieldId: "",
		fieldPriority: "Regular Turn (default)",
		density: density ?? 0,
		importDensity: density ?? 0,
		growerPhoneNumber: "",
		jobFlags: [],
		labSubmissionCode: "",
		isPointsAttached: false,
		isReadyToSample: false,
		labInstructions: "",
		submitterEmail: "",
		submitterName: "",
		eventId: "",
		growerIds: [],
		salespersonIds: [],
		edits: "",
		season: "",
		sampleChanges: "",
		labSubmittalId: "",
		companyName: "",
		boundaryAcres: 0,
		status: "Pending",
		statusNumber: 0,
		isBillingApproved: false,
		isBoundaryApproved: false,
		isBoundaryChanged: false,
		isMarkedReady: false,
		isPointsChanged: false,
		isTestJob: DEBUG,
		dateCreated: new Date(),
		lastCrop: undefined,
		nextCrop: undefined,
		yieldGoal: "",
		numberOfSamples: undefined,
		attachments: {
			pointsSHP: [],
			boundarySHP: [],
			boundaryGeoJSON: [],
			zonesSHP: [],
			executedPointsSHP: [],
			executedBoundarySHP: [],
			executedPointsBoundarySHP: [],
			labPDF: [],
			labCSV: [],
			labResults: [],
			fieldNotes: [],
		},
		files: {
			pointsSHP: undefined,
			pointsGeoJSON: undefined,
			boundarySHP: undefined,
			boundaryGeoJSON: undefined,
			zonesSHP: undefined,
			zonesGeoJSON: undefined,
			executedPointsSHP: undefined,
			executedBoundarySHP: undefined,
			executedPointsBoundarySHP: undefined,
			labPDF: undefined,
			labCSV: undefined,
			labResults: undefined,
			fieldNotes: undefined,
		},
		fileOptions: {
			pointsSHP: undefined,
			centroidsSHP: undefined,
			coresSHP: undefined,
			boundarySHP: undefined,
			zonesSHP: undefined,
			boundaryOptions: undefined,
			pointsOptions: undefined,
			zonesOptions: undefined,
			pointsGeoJSON: undefined,
			boundaryGeoJSON: undefined,
			zonesGeoJSON: undefined,
			executedPointsSHP: undefined,
			executedBoundarySHP: undefined,
			executedPointsBoundarySHP: undefined,
			labPDF: undefined,
			labCSV: undefined,
			labResults: undefined,
			fieldNotes: undefined,
		},
	} as Job;
}
